import React, { useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { ArrowUpRight, ChevronDown, Shield } from "lucide-react";

// ============================================================================
// TAILWIND-ONLY ADMIN CARD NAV
// Keine externe CSS-Datei - alle Styles sind inline Tailwind-Klassen
// ============================================================================

export interface AdminNavLink {
  label: string;
  href?: string;
  onClick?: () => void;
  icon?: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  badge?: number;
  badgeColor?: string;
}

export interface AdminNavGroup {
  label: string;
  links: AdminNavLink[];
}

export interface AdminNavCategory {
  label: string;
  icon?: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  bgColor?: string;
  textColor?: string;
  links?: AdminNavLink[];
  groups?: AdminNavGroup[];
  sectionBadge?: number;
}

// Hilfsfunktion: Einzelnen Link rendern
function renderLink(
  link: AdminNavLink,
  key: string,
  isMobileFullscreen: boolean,
  handleLinkClick: (link: AdminNavLink) => void
) {
  const LinkContent = (
    <>
      <ArrowUpRight
        className={`
          w-3.5 h-3.5 flex-shrink-0 transition-colors
          ${isMobileFullscreen
            ? 'text-gray-400 group-hover:text-brand'
            : 'text-gray-400 dark:text-gray-500 group-hover:text-brand'
          }
        `}
        aria-hidden="true"
      />
      <span className={`
        flex-1 text-sm transition-colors
        ${isMobileFullscreen
          ? 'text-gray-700 dark:text-gray-200 group-hover:text-gray-900 dark:group-hover:text-white'
          : 'text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100'
        }
      `}>
        {link.label}
      </span>
      {link.badge && link.badge > 0 && (
        <span
          className="min-w-[18px] h-[18px] px-1.5 text-[10px] font-semibold rounded-full flex items-center justify-center text-gray-900"
          style={{ backgroundColor: link.badgeColor || "#F7931A" }}
        >
          {link.badge}
        </span>
      )}
    </>
  );

  const linkClasses = `
    group flex items-center gap-2 px-2 py-2 rounded-md transition-colors no-underline
    ${isMobileFullscreen
      ? 'hover:bg-gray-100 dark:hover:bg-white/10'
      : 'hover:bg-gray-100 dark:hover:bg-white/5'
    }
  `;

  if (link.href) {
    return (
      <Link
        key={`link-${key}`}
        className={linkClasses}
        to={link.href}
        onClick={() => handleLinkClick(link)}
      >
        {LinkContent}
      </Link>
    );
  }

  return (
    <button
      key={`link-${key}`}
      className={`${linkClasses} bg-transparent border-none cursor-pointer text-left w-full`}
      onClick={() => handleLinkClick(link)}
    >
      {LinkContent}
    </button>
  );
}

export interface AdminCardNavTWProps {
  categories: AdminNavCategory[];
  totalNotifications?: number;
  className?: string;
  onNavigate?: () => void;
  inline?: boolean;
  /** Vollbild-Modus auf Mobile - optimiertes Layout */
  isMobileFullscreen?: boolean;
}

export const AdminCardNavTW: React.FC<AdminCardNavTWProps> = ({
  categories,
  totalNotifications = 0,
  className = "",
  onNavigate,
  inline = false,
  isMobileFullscreen = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement[]>([]);
  const tlRef = useRef<gsap.core.Timeline | null>(null);
  const isAnimatingRef = useRef(false);
  const isClosingRef = useRef(false);

  const calculateHeight = () => {
    const menuEl = menuRef.current;
    if (!menuEl) return 320;

    const contentEl = menuEl.querySelector("[data-content]") as HTMLElement;
    if (contentEl) {
      const wasVisibility = contentEl.style.visibility;
      const wasPointerEvents = contentEl.style.pointerEvents;
      const wasPosition = contentEl.style.position;
      const wasHeight = contentEl.style.height;

      contentEl.style.visibility = "visible";
      contentEl.style.pointerEvents = "auto";
      contentEl.style.position = "static";
      contentEl.style.height = "auto";
      contentEl.offsetHeight;

      const padding = 24;
      const contentHeight = contentEl.scrollHeight;

      contentEl.style.visibility = wasVisibility;
      contentEl.style.pointerEvents = wasPointerEvents;
      contentEl.style.position = wasPosition;
      contentEl.style.height = wasHeight;

      return contentHeight + padding;
    }
    return 320;
  };

  const createTimeline = () => {
    const menuEl = menuRef.current;
    if (!menuEl) return null;

    gsap.set(menuEl, { height: 0, opacity: 0, overflow: "hidden" });
    gsap.set(cardsRef.current, { y: 30, opacity: 0 });

    const tl = gsap.timeline({ paused: true });

    tl.to(menuEl, {
      height: calculateHeight,
      opacity: 1,
      duration: 0.35,
      ease: "power3.out",
    });

    tl.to(
      cardsRef.current,
      {
        y: 0,
        opacity: 1,
        duration: 0.3,
        ease: "power3.out",
        stagger: 0.06,
      },
      "-=0.15"
    );

    return tl;
  };

  useLayoutEffect(() => {
    const tl = createTimeline();
    tlRef.current = tl;

    return () => {
      tl?.kill();
      tlRef.current = null;
    };
  }, [categories]);

  useLayoutEffect(() => {
    const handleResize = () => {
      if (isAnimatingRef.current) return;
      if (!menuRef.current) return;

      if (isOpen) {
        const newHeight = calculateHeight();
        gsap.set(menuRef.current, { height: newHeight });
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        closeMenu();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const toggleMenu = () => {
    if (!isOpen) {
      openMenu();
    } else {
      closeMenu();
    }
  };

  const openMenu = () => {
    if (isOpen) return;

    let tl = tlRef.current;

    if (!tl || isAnimatingRef.current) {
      tl?.kill();
      tl = createTimeline();
      tlRef.current = tl;
    }

    if (!tl) return;

    isAnimatingRef.current = true;
    setIsOpen(true);

    tl.eventCallback("onComplete", () => {
      isAnimatingRef.current = false;
    });
    tl.play(0);
  };

  const closeMenu = () => {
    if (!isOpen || isClosingRef.current) return;

    isClosingRef.current = true;

    const tl = tlRef.current;
    if (!tl) {
      setIsOpen(false);
      isClosingRef.current = false;
      return;
    }

    isAnimatingRef.current = true;

    const finishClose = () => {
      setIsOpen(false);
      isAnimatingRef.current = false;
      isClosingRef.current = false;
    };

    tl.eventCallback("onReverseComplete", finishClose);
    setTimeout(finishClose, 400);
    tl.reverse();
  };

  const setCardRef = (i: number) => (el: HTMLDivElement | null) => {
    if (el) cardsRef.current[i] = el;
  };

  const handleLinkClick = (link: AdminNavLink) => {
    if (link.onClick) {
      link.onClick();
    }
    closeMenu();
    onNavigate?.();
  };

  // Render content (shared between inline and dropdown)
  const renderContent = () => (
    <div
      data-content
      className={`
        grid gap-4
        ${isMobileFullscreen
          ? 'grid-cols-1 sm:grid-cols-2'
          : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
        }
      `}
    >
      {categories.map((category, idx) => (
        <div
          key={`${category.label}-${idx}`}
          ref={setCardRef(idx)}
          className={`
            rounded-lg p-4 border
            ${isMobileFullscreen
              ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-white/10'
              : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-white/5'
            }
          `}
        >
          {/* Card Header */}
          <div className={`
            flex items-center gap-2 mb-3 pb-2 border-b
            ${isMobileFullscreen
              ? 'border-gray-200 dark:border-white/10'
              : 'border-gray-200 dark:border-white/10'
            }
          `}>
            {category.icon && (
              <category.icon className={`
                w-4 h-4
                ${isMobileFullscreen ? 'text-brand' : 'text-brand'}
              `} />
            )}
            <span className={`
              text-xs font-semibold uppercase tracking-wide
              ${isMobileFullscreen
                ? 'text-gray-700 dark:text-gray-300'
                : 'text-gray-700 dark:text-gray-400'
              }
            `}>
              {category.label}
            </span>
            {category.sectionBadge && category.sectionBadge > 0 && (
              <span className="ml-auto min-w-[18px] h-[18px] px-1.5 text-[10px] font-semibold bg-brand text-gray-900 rounded-full flex items-center justify-center">
                {category.sectionBadge}
              </span>
            )}
          </div>

          {/* Card Links */}
          {category.groups ? (
            // Gruppierte Links mit Sub-Headern
            <div className="flex flex-col">
              {category.groups.map((group, gi) => (
                <div key={`${group.label}-${gi}`}>
                  {gi > 0 && (
                    <div className="border-t border-gray-200 dark:border-white/10 my-2" />
                  )}
                  <div className="px-2 pt-1.5 pb-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                      {group.label}
                    </span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    {group.links.map((link, li) => renderLink(link, `${gi}-${li}`, isMobileFullscreen, handleLinkClick))}
                  </div>
                </div>
              ))}
            </div>
          ) : category.links ? (
            // Flache Links (bestehend)
            <div className="flex flex-col gap-1">
              {category.links.map((link, i) => renderLink(link, String(i), isMobileFullscreen, handleLinkClick))}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );

  // Inline mode: render content directly without toggle button
  if (inline) {
    return (
      <div
        className={`${isMobileFullscreen ? 'w-full' : 'min-w-[320px]'} ${className}`}
        ref={containerRef}
      >
        {renderContent()}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Trigger Button */}
      <button
        onClick={toggleMenu}
        className={`
          flex items-center gap-2 px-4 py-2.5 rounded-lg
          bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-200
          font-medium text-sm cursor-pointer
          transition-all duration-150
          hover:bg-gray-200 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-white/20
          ${isOpen ? "bg-gray-200 dark:bg-gray-700 border-gray-300 dark:border-white/20" : ""}
        `}
      >
        <Shield className="w-4 h-4 text-brand" />
        <span>Admin Panel</span>
        {totalNotifications > 0 && (
          <span className="min-w-[20px] h-5 px-1.5 text-[10px] font-semibold bg-brand text-gray-900 rounded-full flex items-center justify-center ml-2">
            {totalNotifications}
          </span>
        )}
        <ChevronDown
          className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {/* Expandable Menu */}
      <div
        ref={menuRef}
        className="absolute top-full left-0 mt-2 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-white/10 shadow-xl overflow-hidden"
        style={{ minWidth: 400 }}
      >
        <div className="p-4">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default AdminCardNavTW;
